import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { SettingsService } from '../services/settings.service';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  constructor(private appSettings: SettingsService, private http: HttpClient) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    let authToken = `Bearer ${this.appSettings.configSettings.token}`;
    let authKey = `${this.appSettings.configSettings.authKey}`;

    if (authToken) {
      request = request.clone({
        setHeaders: {
          Authorization: authToken,
          auth_key: authKey,
        },
      });
    }

    // Optionally, you can log more details:

    if (request.url.indexOf('/auth/refresh') > -1) {
      //this allows the other ErrorCatches to work
      return next.handle(request);
    } else {
      return next.handle(request).pipe(
        catchError((error) => {
          //console.error('🚨 Intercepted error:', error);

          // ✅ Re-throw the error so it propagates to `handle401Error()`
          if (error.status === 401) {
            return this.handle401Error(request, next);
          }
          error = typeof error === 'function' ? error() : error;
          return throwError(() => error);
        }),
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.refreshToken().pipe(
        switchMap((newToken) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.logout();
          err = typeof err === 'function' ? err() : err;
          return throwError(() => err);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(request, token!))),
      );
    }
  }
  private refreshToken(): Observable<string> {
    let refreshToken = `${this.appSettings.configSettings.refreshToken}`;
    const refreshUrl = `${environment.nestElevationApiUrl}/auth/refresh`;

    return this.http
      .post<any>(refreshUrl, { refreshToken }, { withCredentials: true })
      .pipe(
        tap((response) => {
          if (!response.token) {
            throw new Error('No token in response');
          }
          const { token, refreshToken } = response;
          this.appSettings.set({ token, refreshToken });
        }),
        switchMap((response) => [response.token]),
      );
  }

  logout() {
    this.appSettings.set({
      apiKey: '',
      authKey: '',
      token: '',
      refreshToken: '',
    });
    window.location.reload();
  }
}
